import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { FaMapMarkerAlt, FaSearch, FaArrowLeft, FaChevronDown, FaChevronUp, FaCalendarAlt, FaFilter } from 'react-icons/fa';
import { motion } from 'framer-motion';
import DatePicker from 'react-datepicker';
import HotelCard from '../components/HotelCard';
import SearchResultsMap from '../components/SearchResultsMap';
import { roomsAPI } from '../services/meetingsAPI';
import { getApiUrl } from '../config/api';
import { savePropertyActivity } from '../utils/propertyActivities';
import { isDiscountVisibleToGuest } from '../utils/offerStatus';
import 'react-datepicker/dist/react-datepicker.css';
import './SearchResultsPage.css';

const formatLocalYMD = (dateLike) => {
  const d = new Date(dateLike);
  if (Number.isNaN(d.getTime())) return null;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const toYMD = (dateObj) => {
  if (!dateObj) return null;
  try {
    return formatLocalYMD(dateObj);
  } catch {
    return null;
  }
};

const ymdToDate = (ymd) => {
  if (!ymd) return null;
  try {
    return new Date(`${ymd}T00:00:00`);
  } catch {
    return null;
  }
};

const parseResults = () => {
  try {
    const raw = localStorage.getItem('searchResults');
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const classifyInventorySource = (source) => {
  const normalized = String(source || '').toLowerCase().trim();
  if (normalized === 'api' || normalized === 'external' || normalized === 'serpapi') return 'api';
  if (normalized === 'manual' || normalized === 'host' || normalized === 'internal' || normalized === 'meeting-room') return 'manual';
  return 'manual';
};

const normalizeHotelInventorySource = (hotel) => {
  if (!hotel) return hotel;
  const originSource = hotel.originSource || hotel.source || 'manual';
  return {
    ...hotel,
    source: classifyInventorySource(originSource),
    originSource,
  };
};

const SearchResultsPage = () => {
  const navigate = useNavigate();
  const [searchFilter, setSearchFilter] = useState('');
  const [selectedPropertyType, setSelectedPropertyType] = useState('all');
  const [priceRange, setPriceRange] = useState([0, 100000]);
  const [minBedrooms, setMinBedrooms] = useState(0);
  const [minBathrooms, setMinBathrooms] = useState(0);
  const [minGuests, setMinGuests] = useState(0);
  const [selectedAmenities, setSelectedAmenities] = useState([]);
  const [hasDiscountOnly, setHasDiscountOnly] = useState(false);
  const [minRating, setMinRating] = useState(0);
  const [cancellationFilter, setCancellationFilter] = useState('');
  const [sortBy, setSortBy] = useState('popularity');
  const [activeStayTab, setActiveStayTab] = useState('all');
  const [showFiltersMobile, setShowFiltersMobile] = useState(false);
  const [selectedHotelId, setSelectedHotelId] = useState(null);
  const [movingRoomAlertIndex, setMovingRoomAlertIndex] = useState(0);
  const [filterOpen, setFilterOpen] = useState({
    hotelName: true,
    price: true,
    propertyTypes: true,
    starCategory: true,
    amenities: true,
    more: false
  });
  const toggleFilterSection = (key) => {
    setFilterOpen(prev => ({ ...prev, [key]: !prev[key] }));
  };

  // Search bar state (active, workable)
  const [results, setResults] = useState(() => parseResults());
  const [searchCardLocation, setSearchCardLocation] = useState('');
  const [checkIn, setCheckIn] = useState(null);
  const [checkOut, setCheckOut] = useState(null);
  const [guestData, setGuestData] = useState({ rooms: 1, adults: 2, children: 0 });
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState('');

  // Map global search property type to results page filter id (e.g. 'guest house' -> 'guesthouse')
  const normalizePropertyType = (type) => {
    if (!type || type === 'all') return 'all';
    const t = String(type).toLowerCase().trim();
    if (t === 'guest house') return 'guesthouse';
    const valid = ['hotel', 'villa', 'homestay', 'pg', 'resort', 'apartment', 'guesthouse', 'other', 'meeting room'];
    return valid.includes(t) ? t : 'all';
  };

  // Initialize search bar and property type filter from stored results (from global search)
  useEffect(() => {
    const stored = parseResults();
    if (stored?.query?.city) setSearchCardLocation(stored.query.city);
    else if (stored?.query?.input) setSearchCardLocation(stored.query.input);
    if (stored?.searchParams) {
      if (stored.searchParams.checkIn) setCheckIn(ymdToDate(stored.searchParams.checkIn));
      if (stored.searchParams.checkOut) setCheckOut(ymdToDate(stored.searchParams.checkOut));
      if (stored.searchParams.guests) {
        const g = stored.searchParams.guests;
        setGuestData({ rooms: g.rooms ?? 1, adults: g.adults ?? 2, children: g.children ?? 0 });
      }
      // Apply property type filter from global search (hotel, guest house, pg, villa, apartment, meeting room)
      if (stored.searchParams.propertyType) {
        setSelectedPropertyType(normalizePropertyType(stored.searchParams.propertyType));
      }
    }
  }, []);

  // Lock body scroll when mobile filter drawer is open
  useEffect(() => {
    if (showFiltersMobile) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = prev; };
    }
  }, [showFiltersMobile]);

  const runSearch = useCallback(async () => {
    const location = (searchCardLocation || '').trim();
    if (!location) {
      setSearchError('Please enter a city or hotel name');
      return;
    }
    setSearchError('');
    setSearchLoading(true);
    try {
      const queryParams = new URLSearchParams({
        q: location,
        limit: '100',
        minPrice: String(priceRange[0]),
        maxPrice: String(priceRange[1])
      });
      if (selectedPropertyType && selectedPropertyType !== 'all') {
        const category = selectedPropertyType === 'guesthouse' ? 'guest house' : selectedPropertyType;
        queryParams.set('category', category);
      }
      const searchUrl = process.env.NODE_ENV === 'development'
        ? `/api/search?${queryParams.toString()}`
        : `${getApiUrl('/api/search')}?${queryParams.toString()}`;
      const response = await fetch(searchUrl, { method: 'GET', headers: { 'Content-Type': 'application/json' } });
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const text = await response.text();
        throw new Error(`Server returned non-JSON (${response.status})`);
      }
      const json = await response.json();
      if (!response.ok || !json?.success) {
        throw new Error(json?.message || 'Search failed');
      }
      const enhancedResults = {
        ...json,
        query: { ...json.query, city: location, input: location },
        searchParams: {
          location,
          checkIn: toYMD(checkIn),
          checkOut: toYMD(checkOut),
          guests: guestData,
          propertyType: selectedPropertyType
        }
      };
      localStorage.setItem('searchResults', JSON.stringify(enhancedResults));
      setResults(enhancedResults);
    } catch (err) {
      console.error('Search error:', err);
      setSearchError(err?.message || 'Search failed. Try again.');
    } finally {
      setSearchLoading(false);
    }
  }, [searchCardLocation, checkIn, checkOut, guestData, priceRange, selectedPropertyType]);
  const [meetingRooms, setMeetingRooms] = useState([]);
  const [loadingMeetingRooms, setLoadingMeetingRooms] = useState(false);
  
  // NO PAGINATION - Show all results always
  // Fetch meeting rooms when "Meeting Room" filter is selected
  useEffect(() => {
    if (selectedPropertyType === 'meeting room') {
      const fetchMeetingRooms = async () => {
        setLoadingMeetingRooms(true);
        try {
          const response = await roomsAPI.getRooms();
          if (response.data) {
            // Filter for host-added meeting rooms (isOfficeRoom: true)
            const hostRooms = response.data.filter(room => room.isOfficeRoom === true);
            // Transform to property format
            const transformedRooms = hostRooms.map(room => ({
              id: room._id,
              _id: room._id,
              name: room.name,
              city: room.location?.city || 'Unknown',
              address: room.location?.address || '',
              images: room.images && room.images.length > 0 ? room.images : [],
              rating: room.rating || 0,
              reviews: room.reviews || 0,
              pricePerNight: room.pricePerHour ? room.pricePerHour * 24 : 0,
              currency: 'INR',
              type: 'Meeting Room',
              location: room.location || { lat: null, lng: null },
              isOfficeRoom: true,
              capacity: room.capacity,
              amenities: room.amenities || [],
              description: room.description || '',
              source: 'manual',
              originSource: 'meeting-room',
            }));
            setMeetingRooms(transformedRooms);
          }
        } catch (error) {
          console.error('Error fetching meeting rooms:', error);
          setMeetingRooms([]);
        } finally {
          setLoadingMeetingRooms(false);
        }
      };
      fetchMeetingRooms();
    } else {
      setMeetingRooms([]);
    }
  }, [selectedPropertyType]);
  
  // Property types aligned with host upload (AddPropertyModal): Hotel, Villa, Homestay, PG/Hostel, Resort, Apartment, Guesthouse, Other + Meeting Rooms
  const propertyTypes = [
    { id: 'all', label: 'All Properties', icon: '🏨' },
    { id: 'hotel', label: 'Hotels', icon: '🏨' },
    { id: 'villa', label: 'Villa', icon: '🏡' },
    { id: 'homestay', label: 'Homestays', icon: '🏡' },
    { id: 'pg', label: 'PG / Hostel', icon: '🏠' },
    { id: 'resort', label: 'Resort', icon: '🏖️' },
    { id: 'apartment', label: 'Apartments', icon: '🏢' },
    { id: 'guesthouse', label: 'Guesthouse', icon: '🏠' },
    { id: 'other', label: 'Other', icon: '🏠' },
    { id: 'meeting room', label: 'Meeting Rooms', icon: '🏢' }
  ];

  // Popular amenities (subset of AddPropertyModal) for filter
  const amenityFilterOptions = [
    'WiFi', 'Air Conditioning', 'Kitchen', 'Free Parking', 'Swimming Pool',
    'Gym', 'Breakfast Included', 'Pets Allowed', 'Dedicated Workspace'
  ];

  // More filters: rating options, cancellation options
  const ratingFilterOptions = [
    { value: 0, label: 'Any rating' },
    { value: 3, label: '3+ stars' },
    { value: 4, label: '4+ stars' },
    { value: 4.5, label: '4.5+ stars' }
  ];
  const cancellationFilterOptions = [
    { value: '', label: 'Any cancellation' },
    { value: 'flexible', label: 'Flexible' },
    { value: 'moderate', label: 'Moderate' },
    { value: 'strict', label: 'Strict' }
  ];
  
  const initialQuery = results?.query;

  // Support both new unified format and old format (backward compatibility)
  const allHotels = useMemo(() => {
    // New unified format: results array
    if (Array.isArray(results?.results)) {
      return results.results.map(normalizeHotelInventorySource);
    }
    
    // Old format: internal and external split
    const internal = results?.results?.internal || [];
    const externalListings = results?.results?.external?.listings || [];
    
    // Combine and map external listings to unified format
    const mappedExternal = externalListings.map(listing => ({
      id: listing.id || listing.place_id || `external-${Date.now()}`,
      name: listing.name,
      city: listing.city || '',
      address: listing.address || listing.display_name || '',
      images: listing.images || [],
      rating: listing.rating || null,
      reviews: listing.reviews || listing.totalRatings || 0,
      pricePerNight: listing.pricePerNight || null,
      currency: listing.currency || 'INR',
      type: listing.type || 'Hotel',
      location: listing.location || (listing.latitude && listing.longitude ? {
        lat: listing.latitude,
        lng: listing.longitude
      } : { lat: null, lng: null }),
      distanceFromCenter: listing.distanceFromCenter || null,
      source: 'external'
    }));
    
    return [...internal, ...mappedExternal].map(normalizeHotelInventorySource);
  }, [results]);

  const query = initialQuery;

  // Filter hotels based on search text
  const filterHotelsBySearch = useCallback((hotels, searchText) => {
    if (!searchText || searchText.trim() === '') {
      return hotels;
    }

    const searchLower = searchText.toLowerCase().trim();
    
    return hotels.filter(hotel => {
      // Search in hotel name
      if (hotel.name && hotel.name.toLowerCase().includes(searchLower)) {
        return true;
      }
      
      // Search in city
      if (hotel.city && hotel.city.toLowerCase().includes(searchLower)) {
        return true;
      }
      
      // Search in address
      if (hotel.address && hotel.address.toLowerCase().includes(searchLower)) {
        return true;
      }
      
      // Search in property type
      if (hotel.type && hotel.type.toLowerCase().includes(searchLower)) {
        return true;
    }

      return false;
    });
  }, []);

  // Filter by property type (aligned with host upload options)
  const filteredHotels = useMemo(() => {
    if (selectedPropertyType === 'meeting room') {
      return meetingRooms;
    }
    if (selectedPropertyType === 'all') {
      return allHotels;
    }
    return allHotels.filter(hotel => {
      const hotelType = (hotel.type || '').toLowerCase();
      if (selectedPropertyType === 'hotel') return hotelType.includes('hotel');
      if (selectedPropertyType === 'villa') return hotelType.includes('villa');
      if (selectedPropertyType === 'homestay') return hotelType.includes('homestay');
      if (selectedPropertyType === 'pg') return hotelType.includes('pg') || hotelType.includes('hostel') || hotelType.includes('paying guest');
      if (selectedPropertyType === 'resort') return hotelType.includes('resort');
      if (selectedPropertyType === 'apartment') return hotelType.includes('apartment') || hotelType.includes('flat');
      if (selectedPropertyType === 'guesthouse') return hotelType.includes('guesthouse') || hotelType.includes('guest house');
      if (selectedPropertyType === 'other') return hotelType.includes('other') || !/hotel|villa|homestay|hostel|pg|resort|apartment|guesthouse/.test(hotelType);
      return false;
    });
  }, [allHotels, selectedPropertyType, meetingRooms]);

  // Apply price range filter
  const priceFilteredHotels = useMemo(() => {
    const [minP, maxP] = priceRange;
    return filteredHotels.filter(h => {
      const p = h.pricePerNight || 0;
      return p >= minP && p <= maxP;
    });
  }, [filteredHotels, priceRange]);

  // Apply host-upload filters: bedrooms, bathrooms, max guests, amenities, discount, rating, cancellation
  const hostFiltersFilteredHotels = useMemo(() => {
    let list = priceFilteredHotels;
    if (minBedrooms > 0) {
      list = list.filter(h => (h.bedrooms ?? 0) >= minBedrooms);
    }
    if (minBathrooms > 0) {
      list = list.filter(h => (h.bathrooms ?? 0) >= minBathrooms);
    }
    if (minGuests > 0) {
      list = list.filter(h => (h.maxGuests ?? 0) >= minGuests);
    }
    if (selectedAmenities.length > 0) {
      const amenitySet = new Set(selectedAmenities);
      list = list.filter(h => {
        const listAmenities = Array.isArray(h.amenities) ? h.amenities.map(a => (a || '').trim()) : [];
        return amenitySet.every(a => listAmenities.some(la => la.toLowerCase() === a.toLowerCase()));
      });
    }
    if (hasDiscountOnly) {
      list = list.filter((h) => isDiscountVisibleToGuest(h.discount));
    }
    if (minRating > 0) {
      list = list.filter(h => (h.rating ?? 0) >= minRating);
    }
    if (cancellationFilter) {
      const term = cancellationFilter.toLowerCase();
      list = list.filter(h => {
        const policy = (h.policies && h.policies.cancellationPolicy) ? String(h.policies.cancellationPolicy).toLowerCase() : '';
        if (term === 'flexible') return policy.includes('flexible') || policy.includes('24 hours') || policy.includes('1 day');
        if (term === 'moderate') return policy.includes('moderate') || policy.includes('5 days') || policy.includes('48 hours') || policy.includes('7 days');
        if (term === 'strict') return policy.includes('strict') || policy.includes('non-refundable') || policy.includes('50%');
        return true;
      });
    }
    return list;
  }, [priceFilteredHotels, minBedrooms, minBathrooms, minGuests, selectedAmenities, hasDiscountOnly, minRating, cancellationFilter]);

  // Apply search filter (on host-filters-filtered list)
  const searchFilteredHotels = useMemo(() => {
    return filterHotelsBySearch(hostFiltersFilteredHotels, searchFilter);
  }, [hostFiltersFilteredHotels, searchFilter, filterHotelsBySearch]);

  // Sort: popularity (default), price_low, price_high, rating
  const sortedHotels = useMemo(() => {
    const getSourcePriority = (hotel) => {
      const src = String(hotel?.source || '').toLowerCase();
      // DB-backed properties first, API-fetched/external below
      return src === 'manual' ? 0 : 1;
    };

    const sorted = [...searchFilteredHotels];
    if (sortBy === 'price_low') {
      sorted.sort((a, b) => {
        const sourceDiff = getSourcePriority(a) - getSourcePriority(b);
        if (sourceDiff !== 0) return sourceDiff;
        return (a.pricePerNight || 0) - (b.pricePerNight || 0);
      });
    } else if (sortBy === 'price_high') {
      sorted.sort((a, b) => {
        const sourceDiff = getSourcePriority(a) - getSourcePriority(b);
        if (sourceDiff !== 0) return sourceDiff;
        return (b.pricePerNight || 0) - (a.pricePerNight || 0);
      });
    } else if (sortBy === 'rating') {
      sorted.sort((a, b) => {
        const sourceDiff = getSourcePriority(a) - getSourcePriority(b);
        if (sourceDiff !== 0) return sourceDiff;
        return (b.rating || 0) - (a.rating || 0);
      });
    } else {
      sorted.sort((a, b) => {
        const sourceDiff = getSourcePriority(a) - getSourcePriority(b);
        if (sourceDiff !== 0) return sourceDiff;
        const scoreA = (a.rating || 0) * 0.7 + Math.log(a.reviews + 1) * 0.3;
        const scoreB = (b.rating || 0) * 0.7 + Math.log(b.reviews + 1) * 0.3;
        return scoreB - scoreA;
      });
    }
    return sorted;
  }, [searchFilteredHotels, sortBy]);

  const inventoryCounts = useMemo(() => {
    const manual = sortedHotels.filter((hotel) => hotel.source === 'manual').length;
    const api = sortedHotels.filter((hotel) => hotel.source === 'api').length;
    return {
      all: sortedHotels.length,
      manual,
      api,
    };
  }, [sortedHotels]);

  const tabFilteredHotels = useMemo(() => {
    if (activeStayTab === 'manual') {
      return sortedHotels.filter((hotel) => hotel.source === 'manual');
    }
    if (activeStayTab === 'api') {
      return sortedHotels.filter((hotel) => hotel.source === 'api');
    }
    return sortedHotels;
  }, [sortedHotels, activeStayTab]);

  useEffect(() => {
    if (!selectedHotelId) return;
    const exists = tabFilteredHotels.some((hotel) => String(hotel._id || hotel.id) === String(selectedHotelId));
    if (!exists) setSelectedHotelId(null);
  }, [tabFilteredHotels, selectedHotelId]);

  // Move "few rooms left" urgency across cards
  useEffect(() => {
    if (!tabFilteredHotels.length) {
      setMovingRoomAlertIndex(0);
      return undefined;
    }

    setMovingRoomAlertIndex((prev) => prev % tabFilteredHotels.length);
    const roomAlertTimer = window.setInterval(() => {
      setMovingRoomAlertIndex((prev) => (prev + 1) % tabFilteredHotels.length);
    }, 2600);

    return () => window.clearInterval(roomAlertTimer);
  }, [tabFilteredHotels.length]);


  // Check if this is a city-only search - ALWAYS show all results now
  const isCityOnlySearch = useMemo(() => {
    return true; // Always show all results, no pagination
  }, []);


  // Initialize displayed results - SHOW ALL, NO PAGINATION
  // Handle property type filter change
  const handlePropertyTypeChange = useCallback((propertyType) => {
    setSelectedPropertyType(propertyType);
  }, []);

  const toggleAmenity = useCallback((amenity) => {
    setSelectedAmenities(prev =>
      prev.includes(amenity) ? prev.filter(a => a !== amenity) : [...prev, amenity]
    );
  }, []);

  const handleViewDetails = (hotel) => {
    const id = hotel._id || hotel.id;
    const source = hotel.originSource || hotel.source;
    // SerpAPI rooms: save activity then open external booking site
    if (source === 'serpapi' && hotel.link) {
      savePropertyActivity(hotel, null);
      window.open(hotel.link, '_blank', 'noopener,noreferrer');
      return;
    }
    if (source === 'meeting-room' || hotel.isOfficeRoom) {
      navigate(`/meetings/room/${id}`);
    } else if (id) {
      const navParams = new URLSearchParams();
      const navCheckIn = toYMD(checkIn) || results?.searchParams?.checkIn;
      const navCheckOut = toYMD(checkOut) || results?.searchParams?.checkOut;
      const navGuests = guestData?.adults || results?.searchParams?.guests?.adults || null;
      if (navCheckIn && navCheckOut) {
        navParams.set('checkIn', navCheckIn);
        navParams.set('checkOut', navCheckOut);
      }
      if (navGuests) {
        navParams.set('guests', String(navGuests));
      }
      const queryString = navParams.toString();
      navigate(queryString ? `/property/${id}?${queryString}` : `/property/${id}`);
    }
  };

  const getSearchModeLabel = () => {
    if (!query) return null;
    
    // Handle new search modes
    if (query.mode === 'category_city') {
      return 'Category + City Search';
    } else if (query.mode === 'specific_property') {
      return 'Specific Property Search';
    } else if (query.mode === 'category_only') {
      return 'Category Search';
    } else if (query.mode === 'city_only') {
      return 'City Search';
    }
    
    // Handle legacy modes for backward compatibility
    if (query.mode === 'city') {
      return 'City Search';
    } else if (query.mode === 'general') {
      return 'Property Type Search';
    } else if (query.mode === 'property') {
      return 'Hotel / Brand Search';
    }
    
    return null;
  };

  const priceStats = useMemo(() => {
    const prices = allHotels.map(h => h.pricePerNight).filter(Boolean);
    if (prices.length === 0) return null;
    
    return {
      min: Math.min(...prices),
      max: Math.max(...prices),
      avg: Math.round(prices.reduce((a, b) => a + b, 0) / prices.length)
    };
  }, [allHotels]);

  // Counts for filter sidebar (reference image style)
  const propertyTypeCounts = useMemo(() => {
    const matchType = (hotel, typeId) => {
      const t = (hotel.type || '').toLowerCase();
      if (typeId === 'hotel') return t.includes('hotel');
      if (typeId === 'villa') return t.includes('villa');
      if (typeId === 'homestay') return t.includes('homestay');
      if (typeId === 'pg') return t.includes('pg') || t.includes('hostel') || t.includes('paying guest');
      if (typeId === 'resort') return t.includes('resort');
      if (typeId === 'apartment') return t.includes('apartment') || t.includes('flat');
      if (typeId === 'guesthouse') return t.includes('guesthouse') || t.includes('guest house');
      if (typeId === 'other') return t.includes('other') || !/hotel|villa|homestay|hostel|pg|resort|apartment|guesthouse/.test(t);
      return false;
    };
    const counts = {};
    propertyTypes.filter(p => p.id !== 'all' && p.id !== 'meeting room').forEach(p => {
      counts[p.id] = allHotels.filter(h => matchType(h, p.id)).length;
    });
    counts['meeting room'] = meetingRooms.length;
    return counts;
  }, [allHotels, meetingRooms, propertyTypes]);

  const starCategoryCounts = useMemo(() => {
    const r = (h) => h.rating ?? 0;
    return {
      5: allHotels.filter(h => r(h) >= 4.5).length,
      4: allHotels.filter(h => r(h) >= 3.5 && r(h) < 4.5).length,
      3: allHotels.filter(h => r(h) >= 2.5 && r(h) < 3.5).length,
      2: allHotels.filter(h => r(h) >= 1.5 && r(h) < 2.5).length,
      1: allHotels.filter(h => r(h) >= 0.5 && r(h) < 1.5).length
    };
  }, [allHotels]);

  const PRICE_MAX = 100000;

  const sortOptions = [
    { value: 'popularity', label: 'Our top picks' },
    { value: 'price_low', label: 'Price (lowest first)' },
    { value: 'price_high', label: 'Price (highest first)' },
    { value: 'rating', label: 'Best reviewed' },
  ];

  const stayLabel = useMemo(() => {
    const adults = guestData.adults || 2;
    const children = guestData.children || 0;
    let nights = 1;
    if (checkIn && checkOut) {
      const ms = checkOut.getTime() - checkIn.getTime();
      if (ms > 0) nights = Math.max(1, Math.round(ms / (1000 * 60 * 60 * 24)));
    }
    const guestPart =
      children > 0
        ? `${adults} adult${adults !== 1 ? 's' : ''}, ${children} child${children !== 1 ? 'ren' : ''}`
        : `${adults} adult${adults !== 1 ? 's' : ''}`;
    return `${nights} night${nights !== 1 ? 's' : ''}, ${guestPart}`;
  }, [checkIn, checkOut, guestData]);

  const mapSearchLabel = query?.city || query?.input || searchCardLocation || '';

  return (
    <div className="search-results-page search-results-page--list-style search-results-page--booking">
      <main className="search-results-main">
        {/* Top blue bar: page title + breadcrumbs */}
        <div className="list-page-topbar">
          <div className="list-page-topbar-inner">
            <nav className="list-page-breadcrumb" aria-label="Breadcrumb">
              <Link to="/">Back to Home</Link>
            </nav>
          </div>
        </div>

        {/* White search card - active, workable */}
        <div className="list-page-search-card">
          {searchError && (
            <div className="list-page-search-error" role="alert">
              {searchError}
            </div>
          )}
          <div className="list-page-search-inner">
            <div className="list-page-search-field">
              <FaMapMarkerAlt className="list-page-search-icon" />
              <input
                type="text"
                placeholder="Enter City/Hotel"
                value={searchCardLocation || (query?.city || query?.input || '')}
                onChange={(e) => {
                  setSearchCardLocation(e.target.value);
                  setSearchFilter(e.target.value);
                }}
                onFocus={() => setSearchError('')}
                className="list-page-search-input"
              />
            </div>
            <div className="list-page-search-field list-page-search-date-wrap">
              <FaCalendarAlt className="list-page-search-icon list-page-search-icon-date" />
              <DatePicker
                selected={checkIn}
                onChange={(date) => setCheckIn(date)}
                placeholderText="Check In"
                dateFormat="dd MMM yyyy"
                minDate={new Date()}
                className="list-page-search-input list-page-search-date"
              />
            </div>
            <div className="list-page-search-field list-page-search-date-wrap">
              <FaCalendarAlt className="list-page-search-icon list-page-search-icon-date" />
              <DatePicker
                selected={checkOut}
                onChange={(date) => setCheckOut(date)}
                placeholderText="Check Out"
                dateFormat="dd MMM yyyy"
                minDate={checkIn || new Date()}
                className="list-page-search-input list-page-search-date"
              />
            </div>
            <button
              type="button"
              className="list-page-search-btn"
              onClick={runSearch}
              disabled={searchLoading}
            >
              {searchLoading ? 'Searching…' : 'Search'}
            </button>
          </div>
        </div>

        <div className="search-results-body search-results-body--booking">
          {/* Left sidebar - linear filters */}
          <aside className={`filters-sidebar filters-sidebar--collapsible filters-sidebar--booking ${showFiltersMobile ? 'open' : ''}`}>
            <div className="filters-sidebar-inner">
              <div className="filters-sidebar-header">
                <h3>Filter by:</h3>
                <button type="button" className="filters-close-mobile" onClick={() => setShowFiltersMobile(false)} aria-label="Close filters">×</button>
              </div>

              {/* Hotel Name - collapsible */}
              <div className="filter-collapse">
                <button type="button" className="filter-collapse-header" onClick={() => toggleFilterSection('hotelName')} aria-expanded={filterOpen.hotelName}>
                  <span>Hotel Name</span>
                  {filterOpen.hotelName ? <FaChevronUp className="filter-collapse-arrow" /> : <FaChevronDown className="filter-collapse-arrow" />}
                </button>
                {filterOpen.hotelName && (
                  <div className="filter-collapse-content">
                    <div className="filter-hotel-name-input-wrap">
                      <FaSearch className="filter-hotel-name-icon" />
                      <input
                        type="text"
                        placeholder="Search by Hotel Name"
                        value={searchFilter}
                        onChange={(e) => setSearchFilter(e.target.value)}
                        className="filter-hotel-name-input"
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Price - collapsible with range slider */}
              <div className="filter-collapse">
                <button type="button" className="filter-collapse-header" onClick={() => toggleFilterSection('price')} aria-expanded={filterOpen.price}>
                  <span>Price</span>
                  {filterOpen.price ? <FaChevronUp className="filter-collapse-arrow" /> : <FaChevronDown className="filter-collapse-arrow" />}
                </button>
                {filterOpen.price && (
                  <div className="filter-collapse-content">
                    <div className="filter-price-range-display">
                      ₹{priceRange[0].toLocaleString()} – ₹{priceRange[1].toLocaleString()}
                    </div>
                    <div className="filter-price-slider-wrap">
                      <input
                        type="range"
                        min={0}
                        max={PRICE_MAX}
                        value={Math.min(priceRange[0], PRICE_MAX)}
                        onChange={(e) => {
                          const v = Number(e.target.value);
                          setPriceRange([v, Math.max(v, priceRange[1])]);
                        }}
                        className="filter-range-input filter-range-min"
                      />
                      <input
                        type="range"
                        min={0}
                        max={PRICE_MAX}
                        value={Math.min(priceRange[1], PRICE_MAX)}
                        onChange={(e) => {
                          const v = Number(e.target.value);
                          setPriceRange([Math.min(v, priceRange[0]), v]);
                        }}
                        className="filter-range-input filter-range-max"
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Property Types - collapsible, checkboxes with count */}
              <div className="filter-collapse">
                <button type="button" className="filter-collapse-header" onClick={() => toggleFilterSection('propertyTypes')} aria-expanded={filterOpen.propertyTypes}>
                  <span>Property Types</span>
                  {filterOpen.propertyTypes ? <FaChevronUp className="filter-collapse-arrow" /> : <FaChevronDown className="filter-collapse-arrow" />}
                </button>
                {filterOpen.propertyTypes && (
                  <div className="filter-collapse-content">
                    <ul className="filter-checkbox-list">
                      {propertyTypes.filter(t => t.id !== 'all').map((type) => (
                        <li key={type.id}>
                          <label className="filter-checkbox-row">
                            <input
                              type="radio"
                              name="propertyType"
                              checked={selectedPropertyType === type.id}
                              onChange={() => handlePropertyTypeChange(type.id)}
                              className="filter-checkbox-square"
                            />
                            <span className="filter-checkbox-label">{type.label}</span>
                            <span className="filter-checkbox-count">{propertyTypeCounts[type.id] ?? 0}</span>
                          </label>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              {/* Star Category - collapsible, checkboxes with stars and count */}
              <div className="filter-collapse">
                <button type="button" className="filter-collapse-header" onClick={() => toggleFilterSection('starCategory')} aria-expanded={filterOpen.starCategory}>
                  <span>Star Category</span>
                  {filterOpen.starCategory ? <FaChevronUp className="filter-collapse-arrow" /> : <FaChevronDown className="filter-collapse-arrow" />}
                </button>
                {filterOpen.starCategory && (
                  <div className="filter-collapse-content">
                    <ul className="filter-checkbox-list">
                      <li>
                        <label className="filter-checkbox-row">
                          <input type="radio" name="starCategory" checked={minRating === 0} onChange={() => setMinRating(0)} className="filter-checkbox-square" />
                          <span className="filter-checkbox-label">Any rating</span>
                          <span className="filter-checkbox-count">{allHotels.length}</span>
                        </label>
                      </li>
                      {[5, 4, 3, 2, 1].map((star) => (
                        <li key={star}>
                          <label className="filter-checkbox-row">
                            <input
                              type="radio"
                              name="starCategory"
                              checked={minRating === (star - 0.5)}
                              onChange={() => setMinRating(star - 0.5)}
                              className="filter-checkbox-square"
                            />
                            <span className="filter-checkbox-label">{star} Star {'⭐'.repeat(star)}</span>
                            <span className="filter-checkbox-count">{starCategoryCounts[star] ?? 0}</span>
                          </label>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              {/* Amenities - collapsible */}
              <div className="filter-collapse">
                <button type="button" className="filter-collapse-header" onClick={() => toggleFilterSection('amenities')} aria-expanded={filterOpen.amenities}>
                  <span>Amenities</span>
                  {filterOpen.amenities ? <FaChevronUp className="filter-collapse-arrow" /> : <FaChevronDown className="filter-collapse-arrow" />}
                </button>
                {filterOpen.amenities && (
                  <div className="filter-collapse-content">
                    <ul className="filter-checkbox-list filter-list-amenities">
                      {amenityFilterOptions.map((amenity) => (
                        <li key={amenity}>
                          <label className="filter-checkbox-row">
                            <input
                              type="checkbox"
                              checked={selectedAmenities.includes(amenity)}
                              onChange={() => toggleAmenity(amenity)}
                              className="filter-checkbox-square"
                            />
                            <span className="filter-checkbox-label">{amenity}</span>
                          </label>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              {/* More filters - collapsible */}
              <div className="filter-collapse">
                <button type="button" className="filter-collapse-header" onClick={() => toggleFilterSection('more')} aria-expanded={filterOpen.more}>
                  <span>More</span>
                  {filterOpen.more ? <FaChevronUp className="filter-collapse-arrow" /> : <FaChevronDown className="filter-collapse-arrow" />}
                </button>
                {filterOpen.more && (
                  <div className="filter-collapse-content">
                    <div className="filter-group">
                      <label>Min. Bedrooms</label>
                      <input type="number" min="0" max="20" value={minBedrooms || ''} onChange={(e) => setMinBedrooms(Math.max(0, Number(e.target.value) || 0))} className="filter-price-input filter-single-input" placeholder="Any" />
                    </div>
                    <div className="filter-group">
                      <label>Min. Bathrooms</label>
                      <input type="number" min="0" max="20" value={minBathrooms || ''} onChange={(e) => setMinBathrooms(Math.max(0, Number(e.target.value) || 0))} className="filter-price-input filter-single-input" placeholder="Any" />
                    </div>
                    <div className="filter-group">
                      <label>Min. Guests</label>
                      <input type="number" min="0" max="50" value={minGuests || ''} onChange={(e) => setMinGuests(Math.max(0, Number(e.target.value) || 0))} className="filter-price-input filter-single-input" placeholder="Any" />
                    </div>
                    <label className="filter-checkbox-row filter-discount-row">
                      <input type="checkbox" checked={hasDiscountOnly} onChange={(e) => setHasDiscountOnly(e.target.checked)} className="filter-checkbox-square" />
                      <span className="filter-checkbox-label">Has discount / Offer</span>
                    </label>
                    <div className="filter-group">
                      <label>Cancellation</label>
                      <select value={cancellationFilter} onChange={(e) => setCancellationFilter(e.target.value)} className="filter-select">
                        {cancellationFilterOptions.map((opt) => (
                          <option key={opt.value || 'any'} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </aside>
          <div
            className={`filters-overlay ${showFiltersMobile ? 'open' : ''}`}
            onClick={() => setShowFiltersMobile(false)}
            onTouchEnd={() => setShowFiltersMobile(false)}
            role="button"
            tabIndex={-1}
            aria-label="Close filters"
            aria-hidden={!showFiltersMobile}
          />

          <section className="results-section results-section--booking">
            <button
              type="button"
              className="filters-open-mobile"
              onClick={() => setShowFiltersMobile(true)}
              onTouchEnd={() => setShowFiltersMobile(true)}
              aria-label="Open filters"
            >
              <FaFilter className="filters-open-mobile-icon" aria-hidden />
              <span>Filter</span>
            </button>
            <div className="results-section-inner">
              {sortedHotels.length === 0 ? (
                <motion.div
                  className="empty-state-container"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  <div className="empty-state-icon">
                    <FaSearch />
                  </div>
                  <h3 className="empty-state-title">No properties found</h3>
                  <p className="empty-state-subtitle">
                    Try searching for a different location.
                  </p>
                </motion.div>
              ) : (
                <>
                  <div className="list-page-results-header">
                    <div className="list-page-results-title-row">
                      <h2 className="list-page-results-count">
                        {query?.city || query?.input || 'Search'}: {tabFilteredHotels.length} Accommodations found
                      </h2>
                    </div>
                    <div className="list-page-sort-row">
                      <label htmlFor="sort-results" className="list-page-sort-label">Sort By:</label>
                      <select
                        id="sort-results"
                        value={sortBy}
                        onChange={(e) => setSortBy(e.target.value)}
                        className="list-page-sort-select"
                      >
                        {sortOptions.map((opt) => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="stays-segmented-tabs" role="tablist" aria-label="Stay source filter">
                    <button
                      type="button"
                      role="tab"
                      aria-selected={activeStayTab === 'all'}
                      className={`stays-segmented-tab ${activeStayTab === 'all' ? 'active' : ''}`}
                      onClick={() => setActiveStayTab('all')}
                    >
                      <span className="stays-segmented-tab__label">All Stays</span>
                      <span className="stays-segmented-tab__count">{inventoryCounts.all}</span>
                    </button>
                    <button
                      type="button"
                      role="tab"
                      aria-selected={activeStayTab === 'manual'}
                      className={`stays-segmented-tab ${activeStayTab === 'manual' ? 'active' : ''}`}
                      onClick={() => setActiveStayTab('manual')}
                    >
                      <span className="stays-segmented-tab__label">🏨 Partner Stays</span>
                      <span className="stays-segmented-tab__count">{inventoryCounts.manual}</span>
                    </button>
                    <button
                      type="button"
                      role="tab"
                      aria-selected={activeStayTab === 'api'}
                      className={`stays-segmented-tab ${activeStayTab === 'api' ? 'active' : ''}`}
                      onClick={() => setActiveStayTab('api')}
                    >
                      <span className="stays-segmented-tab__label">⚡ Live Inventory</span>
                      <span className="stays-segmented-tab__count">{inventoryCounts.api}</span>
                    </button>
                  </div>

                  {tabFilteredHotels.length === 0 ? (
                    <motion.div
                      className="empty-state-container"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                    >
                      <div className="empty-state-icon">
                        <FaSearch />
                      </div>
                      <h3 className="empty-state-title">No stays in this tab</h3>
                      <p className="empty-state-subtitle">
                        Try another tab or adjust your filters.
                      </p>
                    </motion.div>
                  ) : (
                    <motion.div
                      className="results-list results-list--booking"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                    >
                      {tabFilteredHotels.map((hotel, index) => {
                        const hid = hotel.id || hotel._id;
                        return (
                          <motion.div
                            key={hid}
                            id={`hotel-card-${hid}`}
                            initial={{ opacity: 0, y: 8 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: Math.min(index * 0.03, 0.3) }}
                            onMouseEnter={() => setSelectedHotelId(hid)}
                          >
                            <HotelCard
                              variant="booking"
                              hotel={hotel}
                              stayLabel={stayLabel}
                              urgencyText={
                                index === movingRoomAlertIndex && hotel.pricePerNight
                                  ? 'Only a few rooms left at this price'
                                  : ''
                              }
                              selected={String(selectedHotelId) === String(hid)}
                              onViewDetails={handleViewDetails}
                            />
                          </motion.div>
                        );
                      })}
                    </motion.div>
                  )}
                </>
              )}
            </div>
          </section>

          <SearchResultsMap
            hotels={tabFilteredHotels}
            selectedHotelId={selectedHotelId}
            checkIn={checkIn}
            checkOut={checkOut}
            onSelectHotel={(hotel) => {
              const hid = hotel._id || hotel.id;
              setSelectedHotelId(hid);
            }}
            onClearSelection={() => setSelectedHotelId(null)}
            onViewDetails={handleViewDetails}
            searchQuery={mapSearchLabel}
          />
        </div>
      </main>
    </div>
  );
};

export default SearchResultsPage;


